/**
 * Decorators
 * Decorator is a function that decorates a form by subscribing to it and making changes as the form state changes, and returns an Unsubscribe function to detach itself from the form.
 * {@link https://final-form.org/docs/final-form/types/Decorator}
 */

import createDecorator from 'final-form-calculate';
import get from 'lodash.get';

// Utils
import { formatMyTimesheetEntry } from 'utils/my-timesheets';
import { formatEntryForForm } from 'utils/timesheets';

// Types
import { NewValues } from './types';

const myTimesheetEntryCalculation = {
    field: /(startTime|endTime|unpaidBreakMinutes|units|rate|paidBreakMinutes|hasEditedPaidBreak|hasEditedUnpaidBreak)/,
    updates: (value: any, name: string, allValues: any): NewValues => {
        const entry = formatMyTimesheetEntry({
            ...allValues,
            [name]: value
        });

        return entry;
    }
};

const myTimesheetEntryDecorator = createDecorator(myTimesheetEntryCalculation);

const timesheetEntryCalculation = {
    field: /(startTime|endTime|unpaidBreakMinutes|workTypeCompositeKey|units|rate|paidBreakMinutes|hasEditedPaidBreak|hasEditedUnpaidBreak)/,
    updates: (value: any, name: string, allValues: any): NewValues => {
        const entry = formatEntryForForm({
            ...allValues,
            [name]: value
        });

        return entry;
    }
};

const timesheetEntryDecorator = createDecorator(timesheetEntryCalculation);

const timesheetEntriesCalculation = {
    field: /\.(startTime|endTime|unpaidBreakMinutes|workTypeCompositeKey|units|rate|paidBreakMinutes)/,
    updates: (value: any, name: string, allValues: any): NewValues => {
        const [uuid, field] = name.split('.');

        const previousEntry = get(allValues, uuid);

        if (previousEntry && previousEntry.personId) {
            const entry = formatEntryForForm({
                ...previousEntry,
                [field]: value,
                // We need to know if a break is edited so that we can prevent them from being auto calculated
                hasEditedPaidBreak:
                    previousEntry.hasEditedPaidBreak ||
                    field === 'paidBreakMinutes',
                hasEditedUnpaidBreak:
                    previousEntry.hasEditedUnpaidBreak ||
                    field === 'unpaidBreakMinutes'
            });

            return {
                [uuid]: entry
            };
        }

        return {};
    }
};

const timesheetEntriesDecorator = createDecorator(timesheetEntriesCalculation);

export {
    myTimesheetEntryCalculation,
    myTimesheetEntryDecorator,
    timesheetEntryCalculation,
    timesheetEntryDecorator,
    timesheetEntriesCalculation,
    timesheetEntriesDecorator
};
